// Page: CreateStockVideo
// Purpose: Admin page to create a stock video record. Integrates AI prefill
// modal and a form that uploads to storage via signed URL. Requires admin.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ArrowLeft, Video, Sparkles } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import StockVideoForm from '../../../components/admin/stockVideos/StockVideoForm';
import { StockVideoAIModal } from '../../../components/admin/stockVideos/StockVideoAIModal';
import Footer from '../../../components/shared/Footer';
import { createStockVideo } from '../../../api/media/stockVideosApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function CreateStockVideo() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [submitError, setSubmitError] = useState('');

  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  const openAIModal = () => {
    setIsAIModalOpen(true);
  };

  const handleAIGenerateSuccess = (aiGeneratedData) => {
    console.log('AI generated stock video data:', aiGeneratedData);
    
    // Update form data with AI generated content
    setFormData(prevData => ({
      ...prevData,
      ...aiGeneratedData
    }));
    
    addToast('Stock video data has been generated and populated in the form!', 'success');
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      console.log('[CreateStockVideo] Submitting stock video creation request');
      
      const response = await createStockVideo(formData);
      
      if (response && response.stockVideoId) {
        console.log('[CreateStockVideo] Stock video created successfully:', response.stockVideoId);
        addToast('Your stock video has been created successfully!', 'success');
        
        // Navigate back to manage stock videos or admin
        navigate('/app/private/manageStockVideos');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('[CreateStockVideo] Error creating stock video:', error);
      
      let errorMessage = 'Failed to create stock video. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading create stock video..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/stock-videos')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Stock Videos
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create Stock Video</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload and create a new stock video for the library.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/private/manageStockVideos')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
          >
            Manage Stock Videos
          </button>
          <button
            onClick={openAIModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Sparkles className="h-4 w-4" />
            Use AI
          </button>
        </div>
      </div>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
        {submitError && (
          <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Creating Stock Video</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <StockVideoForm 
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          initialData={formData}
          submitButtonText="Create Stock Video"
        />
      </div>
      
      {/* AI Modal */}
      <StockVideoAIModal 
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onGenerateSuccess={handleAIGenerateSuccess}
      />

      <Footer />
    </div>
  );
} 